import {
  createAppDraft,
  createCredentialDraft,
  createQuestDraft,
  createQuestKeyFromTitle,
  createRoundDraft,
  createSeasonDraft,
  updateDefaultRoundClosingCopy,
  questWorkspaceTargetKey,
  type QuestCredentialForm,
  type QuestSelectionPreviewForm,
  type QuestWorkspaceForm,
  type QuestWorkspaceIssue,
  type QuestWorkspaceItemRef,
} from './questWorkspace';

export type QuestWorkspaceCollectionKey =
  | 'seasons'
  | 'rounds'
  | 'apps'
  | 'quests'
  | 'credentials';

const uuidPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function createLocalId(prefix: string) {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

export function isPersistedId(id: string) {
  return uuidPattern.test(id);
}

function updateById<T extends { id: string }>(
  items: T[],
  id: string,
  patch: Record<string, unknown>,
) {
  return items.map((item) => (item.id === id ? { ...item, ...patch } : item));
}

function refExists(workspace: QuestWorkspaceForm, ref: QuestWorkspaceItemRef) {
  switch (ref.kind) {
    case 'selection':
    case 'overview':
      return true;
    case 'season':
      return workspace.seasons.some((season) => season.id === ref.id);
    case 'round':
      return workspace.rounds.some((round) => round.id === ref.id);
    case 'app':
      return workspace.apps.some((app) => app.id === ref.id);
    case 'quest':
      return workspace.quests.some((quest) => quest.id === ref.id);
    case 'credential':
      return workspace.credentials.some(
        (credential) => credential.id === ref.id,
      );
  }
}

export function resolveSelection(
  workspace: QuestWorkspaceForm,
  selected: QuestWorkspaceItemRef | null,
): QuestWorkspaceItemRef {
  if (selected && refExists(workspace, selected)) {
    return selected;
  }

  // The Overview is the orientation landing surface — it summarises the whole
  // campaign rather than dropping the admin into an arbitrary leaf form.
  return { kind: 'overview' };
}

export function parentRefOf(
  workspace: QuestWorkspaceForm,
  ref: QuestWorkspaceItemRef,
): QuestWorkspaceItemRef | null {
  switch (ref.kind) {
    case 'round': {
      const round = workspace.rounds.find((item) => item.id === ref.id);
      return round &&
        refExists(workspace, { kind: 'season', id: round.seasonId })
        ? { kind: 'season', id: round.seasonId }
        : null;
    }
    case 'app': {
      const app = workspace.apps.find((item) => item.id === ref.id);
      return app && refExists(workspace, { kind: 'round', id: app.roundId })
        ? { kind: 'round', id: app.roundId }
        : null;
    }
    case 'quest': {
      const quest = workspace.quests.find((item) => item.id === ref.id);
      return quest &&
        refExists(workspace, { kind: 'app', id: quest.roundAppId })
        ? { kind: 'app', id: quest.roundAppId }
        : null;
    }
    case 'credential': {
      const credential = workspace.credentials.find(
        (item) => item.id === ref.id,
      );
      const appId = credential?.scopedAppIds[0];
      return appId && refExists(workspace, { kind: 'app', id: appId })
        ? { kind: 'app', id: appId }
        : null;
    }
    default:
      return null;
  }
}

export function selfAndAncestorKeys(
  workspace: QuestWorkspaceForm,
  ref: QuestWorkspaceItemRef,
): string[] {
  const keys: string[] = [];
  let current: QuestWorkspaceItemRef | null = ref;

  while (current) {
    keys.push(questWorkspaceTargetKey(current));
    current = parentRefOf(workspace, current);
  }

  return keys;
}

function withoutRemovedScopes(
  credentials: QuestCredentialForm[],
  removedAppIds: Set<string>,
) {
  if (removedAppIds.size === 0) {
    return credentials;
  }

  return credentials.flatMap((credential) => {
    if (!credential.scopedAppIds.some((id) => removedAppIds.has(id))) {
      return [credential];
    }

    const scopedAppIds = credential.scopedAppIds.filter(
      (id) => !removedAppIds.has(id),
    );
    return scopedAppIds.length > 0 ? [{ ...credential, scopedAppIds }] : [];
  });
}

export function removeFromWorkspace(
  workspace: QuestWorkspaceForm,
  ref: QuestWorkspaceItemRef,
): QuestWorkspaceForm {
  if (ref.kind === 'selection') {
    return workspace;
  }

  if (ref.kind === 'credential') {
    return {
      ...workspace,
      credentials: workspace.credentials.filter(
        (credential) => credential.id !== ref.id,
      ),
    };
  }

  const removedRoundIds = new Set<string>();
  const removedAppIds = new Set<string>();
  const removedQuestIds = new Set<string>();

  if (ref.kind === 'round') {
    removedRoundIds.add(ref.id);
  }
  if (ref.kind === 'app') {
    removedAppIds.add(ref.id);
  }
  if (ref.kind === 'quest') {
    removedQuestIds.add(ref.id);
  }
  if (ref.kind === 'season') {
    for (const round of workspace.rounds) {
      if (round.seasonId === ref.id) {
        removedRoundIds.add(round.id);
      }
    }
  }
  for (const app of workspace.apps) {
    if (removedRoundIds.has(app.roundId)) {
      removedAppIds.add(app.id);
    }
  }
  for (const quest of workspace.quests) {
    if (removedAppIds.has(quest.roundAppId)) {
      removedQuestIds.add(quest.id);
    }
  }

  const nextWorkspace = {
    ...workspace,
    seasons:
      ref.kind === 'season'
        ? workspace.seasons.filter((season) => season.id !== ref.id)
        : workspace.seasons,
    rounds: workspace.rounds.filter((round) => !removedRoundIds.has(round.id)),
    apps: workspace.apps.filter((app) => !removedAppIds.has(app.id)),
    quests: workspace.quests.filter((quest) => !removedQuestIds.has(quest.id)),
    credentials: withoutRemovedScopes(workspace.credentials, removedAppIds),
  };

  if (
    removedRoundIds.has(workspace.selectionPreview.selectedRoundId) ||
    !nextWorkspace.rounds.some(
      (round) => round.id === nextWorkspace.selectionPreview.selectedRoundId,
    )
  ) {
    return {
      ...nextWorkspace,
      selectionPreview: {
        ...nextWorkspace.selectionPreview,
        selectedRoundId: nextWorkspace.rounds[0]?.id ?? '',
      },
    };
  }

  return nextWorkspace;
}

export function patchWorkspace(
  workspace: QuestWorkspaceForm,
  key: QuestWorkspaceCollectionKey,
  id: string,
  patch: Record<string, unknown>,
): QuestWorkspaceForm {
  const quest =
    key === 'quests'
      ? workspace.quests.find((item) => item.id === id)
      : undefined;
  const normalizedPatch =
    quest && typeof patch.title === 'string'
      ? {
          ...patch,
          ...(!isPersistedId(quest.id)
            ? { questKey: createQuestKeyFromTitle(patch.title) }
            : {}),
          badgeTitle: patch.title,
        }
      : patch;

  const next = {
    ...workspace,
    [key]: updateById(
      workspace[key] as Array<{ id: string }>,
      id,
      normalizedPatch,
    ),
  } as QuestWorkspaceForm;

  if (key === 'rounds') {
    const previousRound = workspace.rounds.find((round) => round.id === id);
    const round = next.rounds.find((item) => item.id === id);
    if (!previousRound || !round) {
      return next;
    }

    return {
      ...next,
      rounds: next.rounds.map((item) =>
        item.id === id
          ? updateDefaultRoundClosingCopy(item, previousRound)
          : item,
      ),
    };
  }

  return next;
}

export function patchSelectionPreview(
  workspace: QuestWorkspaceForm,
  patch: Partial<QuestSelectionPreviewForm>,
): QuestWorkspaceForm {
  return {
    ...workspace,
    selectionPreview: { ...workspace.selectionPreview, ...patch },
  };
}

export function addSeasonToWorkspace(
  workspace: QuestWorkspaceForm,
  id: string,
): QuestWorkspaceForm {
  return {
    ...workspace,
    seasons: [...workspace.seasons, createSeasonDraft(id)],
  };
}

export function addRoundToWorkspace(
  workspace: QuestWorkspaceForm,
  seasonId: string,
  id: string,
): QuestWorkspaceForm {
  return {
    ...workspace,
    rounds: [
      ...workspace.rounds,
      createRoundDraft(
        id,
        seasonId,
        String(
          workspace.rounds.filter((round) => round.seasonId === seasonId)
            .length + 1,
        ),
      ),
    ],
  };
}

export function addAppToWorkspace(
  workspace: QuestWorkspaceForm,
  roundId: string,
  id: string,
  prefill?: Record<string, string>,
): QuestWorkspaceForm {
  return {
    ...workspace,
    apps: [
      ...workspace.apps,
      {
        ...createAppDraft(
          id,
          roundId,
          String(
            workspace.apps.filter((app) => app.roundId === roundId).length,
          ),
        ),
        ...prefill,
      },
    ],
  };
}

export function addQuestToWorkspace(
  workspace: QuestWorkspaceForm,
  appId: string,
  id: string,
): QuestWorkspaceForm {
  return {
    ...workspace,
    quests: [
      ...workspace.quests,
      createQuestDraft(
        id,
        appId,
        String(
          workspace.quests.filter((quest) => quest.roundAppId === appId).length,
        ),
      ),
    ],
  };
}

export function addCredentialToWorkspace(
  workspace: QuestWorkspaceForm,
  appId: string,
  id: string,
): QuestWorkspaceForm {
  return {
    ...workspace,
    credentials: [...workspace.credentials, createCredentialDraft(id, appId)],
  };
}

export type AppMoveEdge = 'before' | 'after';

/**
 * Moves an app before/after a sibling of the same round. Only the round's
 * relative order changes: its apps keep occupying the same positions in the
 * global apps array (rounds can interleave there), and their sortOrder fields
 * are renumbered to the contiguous per-round sequence the backend derives from
 * array order on save. No-op moves return the workspace unchanged so a drop on
 * the same spot never dirties the editor.
 */
export function moveAppWithinRound(
  workspace: QuestWorkspaceForm,
  appId: string,
  targetAppId: string,
  edge: AppMoveEdge,
): QuestWorkspaceForm {
  if (appId === targetAppId) {
    return workspace;
  }

  const app = workspace.apps.find((item) => item.id === appId);
  const target = workspace.apps.find((item) => item.id === targetAppId);
  if (!app || !target || app.roundId !== target.roundId) {
    return workspace;
  }

  const roundApps = workspace.apps.filter(
    (item) => item.roundId === app.roundId,
  );
  const withoutMoved = roundApps.filter((item) => item.id !== appId);
  const targetIndex = withoutMoved.findIndex((item) => item.id === targetAppId);
  const insertIndex = edge === 'before' ? targetIndex : targetIndex + 1;
  const reordered = [
    ...withoutMoved.slice(0, insertIndex),
    app,
    ...withoutMoved.slice(insertIndex),
  ];

  if (reordered.every((item, index) => item === roundApps[index])) {
    return workspace;
  }

  const roundPositions: number[] = [];
  for (const [index, item] of workspace.apps.entries()) {
    if (item.roundId === app.roundId) {
      roundPositions.push(index);
    }
  }

  const apps = [...workspace.apps];
  for (const [position, globalIndex] of roundPositions.entries()) {
    const moved = reordered[position];
    const sortOrder = String(position);
    apps[globalIndex] =
      moved.sortOrder === sortOrder ? moved : { ...moved, sortOrder };
  }

  return { ...workspace, apps };
}

export interface CascadeCounts {
  rounds: number;
  apps: number;
  quests: number;
  credentials: number;
}

/**
 * How many descendant records a removal would also delete, so the confirm
 * affordance can state its blast radius instead of silently nuking a subtree.
 */
export function cascadeCounts(
  workspace: QuestWorkspaceForm,
  ref: QuestWorkspaceItemRef,
): CascadeCounts {
  const removedRoundIds = new Set<string>();
  const removedAppIds = new Set<string>();
  const removedQuestIds = new Set<string>();

  if (ref.kind === 'round') {
    removedRoundIds.add(ref.id);
  }
  if (ref.kind === 'app') {
    removedAppIds.add(ref.id);
  }
  if (ref.kind === 'quest') {
    removedQuestIds.add(ref.id);
  }
  if (ref.kind === 'season') {
    for (const round of workspace.rounds) {
      if (round.seasonId === ref.id) {
        removedRoundIds.add(round.id);
      }
    }
  }
  for (const app of workspace.apps) {
    if (removedRoundIds.has(app.roundId)) {
      removedAppIds.add(app.id);
    }
  }
  for (const quest of workspace.quests) {
    if (removedAppIds.has(quest.roundAppId)) {
      removedQuestIds.add(quest.id);
    }
  }
  const removedCredentials = workspace.credentials.filter(
    (credential) =>
      credential.scopedAppIds.length > 0 &&
      credential.scopedAppIds.every((id) => removedAppIds.has(id)),
  ).length;

  // The selected node itself is reported by its own editor; only count descendants.
  return {
    rounds: ref.kind === 'season' ? removedRoundIds.size : 0,
    apps: removedAppIds.size - (ref.kind === 'app' ? 1 : 0),
    quests: removedQuestIds.size - (ref.kind === 'quest' ? 1 : 0),
    credentials: removedCredentials,
  };
}

export function cascadeSummaryText(counts: CascadeCounts): string {
  const parts: string[] = [];
  if (counts.rounds > 0) {
    parts.push(`${counts.rounds} round${counts.rounds === 1 ? '' : 's'}`);
  }
  if (counts.apps > 0) {
    parts.push(`${counts.apps} app${counts.apps === 1 ? '' : 's'}`);
  }
  if (counts.quests > 0) {
    parts.push(`${counts.quests} quest${counts.quests === 1 ? '' : 's'}`);
  }
  if (counts.credentials > 0) {
    parts.push(
      `${counts.credentials} API key${counts.credentials === 1 ? '' : 's'}`,
    );
  }
  return parts.join(', ');
}

/**
 * Issues roll up to every ancestor so a collapsed parent still signals that
 * something inside it is broken (the VS Code "problem decoration" model).
 * Returns a key -> descendant-issue-count map (self counts are excluded).
 */
export function descendantIssueCounts(
  workspace: QuestWorkspaceForm,
  issues: QuestWorkspaceIssue[],
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const issue of issues) {
    if (issue.target.kind === 'selection' || issue.target.kind === 'overview') {
      continue;
    }
    const ancestorKeys = selfAndAncestorKeys(workspace, issue.target).slice(1);
    for (const key of ancestorKeys) {
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }
  return counts;
}
